import { ArrowLeft, Plus, X } from "lucide-react";
import { LineClampedTooltip } from "@/core/components/line-clamped-tooltip";
import { LoadingSpinner } from "@/core/components/loading-spinner";
import { ScrollArea } from "@/core/components/ui/scroll-area";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { HarnessChatInput } from "@/onboarding/components/harness-chat/harness-chat-input";
import { HarnessChatSuggestedPrompts } from "@/onboarding/components/harness-chat/harness-chat-suggested-prompts";
import { IconButtonWithTooltip } from "@/payroll/ai-script/icon-button-with-tooltip";
import type { AiScriptChatSelectionContext } from "@/payroll/ai-script/types";
import { AccessDeniedState } from "./access-denied-state";
import { ChatBubble } from "./chat-bubble";
import { ChatThreadList } from "./chat-thread-list";
import { LiveAssistantMessage } from "./live-assistant-message";
import { SelectionContextChip } from "./selection-context-chip";
import type { AiScriptExplanationChatSidebarProps } from "./types";
import { useAiScriptExplanationChatSession } from "./use-ai-script-explanation-chat-session";

const PAYROLL_SURFACE = {
  title: "Payroll Chat",
  subtitle: "Chats about this pay period's results",
  composerPlaceholder: "Ask why a technician was paid a certain amount...",
  suggestedPrompts: [
    "Summarize this pay period's results",
    "Who earned the most this period, and why?",
    "Were any technicians paid the hourly floor instead of commission?",
  ],
};

const SelectionContextInputHeader = ({
  context,
  onDismiss,
}: {
  context: AiScriptChatSelectionContext | null;
  onDismiss?: () => void;
}) =>
  context ? (
    <div className="mb-2 flex">
      <SelectionContextChip context={context} onDismiss={onDismiss} />
    </div>
  ) : null;

export const AiScriptExplanationChatSidebar = ({
  businessId,
  runId,
  selectionContext,
  pendingQuestion,
  onQuestionPrepared,
  buildChatPath,
  title = PAYROLL_SURFACE.title,
  subtitle = PAYROLL_SURFACE.subtitle,
  composerPlaceholder = PAYROLL_SURFACE.composerPlaceholder,
  suggestedPrompts = PAYROLL_SURFACE.suggestedPrompts,
  onClearSelectionContext,
  onClose,
}: AiScriptExplanationChatSidebarProps) => {
  const {
    isAccessDenied,
    isListView,
    input,
    messages,
    threads,
    getIsThreadRunning,
    selectedThread,
    attachedContext,
    isBusy,
    isThreadHistoryLoading,
    displayedLiveText,
    progressSteps,
    bottomRef,
    textareaRef,
    handleDraftChange,
    handleOpenThread,
    handleNewChat,
    handleStartNewChatWithMessage,
    handleBackToList,
    handleSend,
  } = useAiScriptExplanationChatSession({
    businessId,
    runId,
    selectionContext,
    pendingQuestion,
    onQuestionPrepared,
    buildChatPath,
  });

  if (isAccessDenied) {
    return <AccessDeniedState onClose={onClose} />;
  }

  if (isListView) {
    return (
      <Stack fullWidth align="stretch" className="h-full min-h-0" gap="none">
        <div className="shrink-0 border-b border-stroke-tertiary bg-card/80 px-4 py-3 backdrop-blur-sm">
          <Stack direction="row" align="center" justify="between" fullWidth>
            <Stack gap="none">
              <Typography variant="subtitleW2">{title}</Typography>
              <Typography variant="bodyW3" textColor="tertiary">
                {subtitle}
              </Typography>
            </Stack>
            <IconButtonWithTooltip label="Close" onClick={onClose}>
              <X className="h-5 w-5" />
            </IconButtonWithTooltip>
          </Stack>
        </div>

        <ChatThreadList
          threads={threads}
          getIsThreadRunning={getIsThreadRunning}
          onOpenThread={handleOpenThread}
        />

        <HarnessChatInput
          draft={input}
          disabled={isBusy}
          textareaRef={textareaRef}
          onDraftChange={handleDraftChange}
          onSend={() => handleStartNewChatWithMessage()}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              handleStartNewChatWithMessage();
            }
          }}
          placeholder={composerPlaceholder}
          header={
            <SelectionContextInputHeader
              context={attachedContext}
              onDismiss={onClearSelectionContext}
            />
          }
        />
      </Stack>
    );
  }

  return (
    <Stack fullWidth align="stretch" className="h-full min-h-0" gap="none">
      <div className="shrink-0 border-b border-stroke-tertiary bg-card/80 px-4 py-3 backdrop-blur-sm">
        <Stack direction="row" align="center" justify="between" fullWidth>
          <Stack
            direction="row"
            align="center"
            gap="sm"
            className="min-w-0 flex-1"
          >
            <IconButtonWithTooltip label="All chats" onClick={handleBackToList}>
              <ArrowLeft className="h-5 w-5" />
            </IconButtonWithTooltip>
            <LineClampedTooltip className="min-w-0 flex-1">
              <Typography variant="subtitleW3">
                {selectedThread?.name ?? "New chat"}
              </Typography>
            </LineClampedTooltip>
          </Stack>
          <Stack direction="row" align="center" gap="xs" className="shrink-0">
            <IconButtonWithTooltip label="New chat" onClick={handleNewChat}>
              <Plus className="h-5 w-5" />
            </IconButtonWithTooltip>
            <IconButtonWithTooltip label="Close" onClick={onClose}>
              <X className="h-5 w-5" />
            </IconButtonWithTooltip>
          </Stack>
        </Stack>
      </div>

      <ScrollArea className="min-h-0 flex-1">
        <div className="px-5 py-4">
          {messages.length === 0 && !isBusy ? (
            isThreadHistoryLoading ? (
              <Stack fullWidth align="center" className="py-8">
                <LoadingSpinner />
              </Stack>
            ) : (
              <HarnessChatSuggestedPrompts
                prompts={suggestedPrompts}
                disabled={isBusy}
                onSend={handleSend}
              />
            )
          ) : (
            messages.map((message, index) => (
              <ChatBubble
                key={message.id}
                message={message}
                prevRole={messages[index - 1]?.role}
              />
            ))
          )}

          {/* Keep the live bubble mounted past isBusy until the typewriter
              finishes and the final message flushes into the transcript. */}
          {(isBusy || displayedLiveText.length > 0) && (
            <LiveAssistantMessage
              displayedLiveText={displayedLiveText}
              progressSteps={progressSteps}
            />
          )}
          <div ref={bottomRef} />
        </div>
      </ScrollArea>

      <HarnessChatInput
        draft={input}
        disabled={isBusy}
        textareaRef={textareaRef}
        onDraftChange={handleDraftChange}
        onSend={() => handleSend()}
        onKeyDown={(event) => {
          if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            handleSend();
          }
        }}
        placeholder={composerPlaceholder}
        header={
          <SelectionContextInputHeader
            context={attachedContext}
            onDismiss={onClearSelectionContext}
          />
        }
      />
    </Stack>
  );
};
